import { createParamDecorator, ExecutionContext } from '@nestjs/common';
import { GqlExecutionContext } from '@nestjs/graphql';
import DataLoader from 'dataloader';
import { ObjectLiteral } from 'typeorm';
import { BaseDataLoader } from './base.dataloader';
import { InjectLoaderInput, GetDataloaderInput } from './types';
import { isObjectInput, validateInjectLoaderInput } from './utils';

//Injected BaseDataloaders will be stored in the context under this key
const BASE_DATALOADERS = 'BASE_DATALOADERS';

/**
 * Decorator for injecting a dataloader into a ResolveField.
 */
export const InjectLoader = createParamDecorator(
  async <V extends ObjectLiteral, K = string>(
    input: InjectLoaderInput<V>,
    context: ExecutionContext
  ) => {
    //TODO: Improve type checking for input
    //Using resolveField's name as the dataloader name
    const dataloaderName = `${context.getClass().name}.${
      context.getHandler().name
    }`;
    validateInjectLoaderInput(input, dataloaderName);
    let baseDataLoaderInput: GetDataloaderInput<V>;
    if (isObjectInput(input)) {
      baseDataLoaderInput = {
        dataloaderName,
        ...input,
      };
    } else {
      //CASE: input is an EntityTarget
      baseDataLoaderInput = {
        dataloaderName,
        resolveType: 'one',
        fromEntity: input,
        fieldName: 'id',
        resultType: 'entity'
      };
    }
    return getDataLoaderFromContext<V, K>(baseDataLoaderInput, context);
  }
);

/**
 * Creates a new BaseDataLoader for the required Dataloader if one doesn't exist in the context,
 * otherwise returns the existing BaseDataLoader.
 * @param inp Input object for getting the dataloader
 * @param inputContext Execution context
 * @returns DataLoader object
 */
async function getDataLoaderFromContext<V extends ObjectLiteral, K>(
  inp: GetDataloaderInput<V>,
  inputContext: ExecutionContext
) {
  const context: any = GqlExecutionContext.create(inputContext).getContext();
  if (context[BASE_DATALOADERS] === undefined) {
    context[BASE_DATALOADERS] = {};
  }
  if (context[BASE_DATALOADERS][inp.dataloaderName] === undefined) {
    context[BASE_DATALOADERS][inp.dataloaderName] = new BaseDataLoader<V, K>(
      inp
    );
  }
  const dataloader: DataLoader<K, V> = await context[BASE_DATALOADERS][
    inp.dataloaderName
  ].getDataLoader();
  return dataloader;
}
